import { useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../../redux/store";
import "../../../css/CreatorDashboardMassTextsPanel.css";
import { Icon } from "@iconify/react";
import { Colors } from "../../../utils/colors";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import SearchBoxContainer from "../../Containers/SearchBoxContainer";
import RectangleButton from "../../Buttons/RectangleButton";
import ConversationPresetModal from "./ConversationPresets/ConversationPresetModal";
import { ConversationPreset } from "@markit/common.types";
import filter from "lodash.filter";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import { ConversationPresetItem } from "../../DisplayItem/ConversationPresetItem";
import { CircularProgress } from "@mui/material";
import DragDropList from "../../Lists/DragDropList";
import DragDropListIcon from "../../Lists/DragDropListIcon";
import { EmptyStateFlatlist } from "../../EmptyStates/EmptyStateFlatlist";
import { API } from "../../../API";
import {
  conversationActions,
  getConversationState,
} from "../../../redux/slices/conversationSlice";

type ConversationPresetsPanelProps = {
  panelTabValue: number;
};

const ConversationPresetsPanel = (props: ConversationPresetsPanelProps) => {
  const { panelTabValue } = props;
  const accountData = useSelector(
    (state: AppState) => state.account.accountData
  );
  const { conversationPresets, loadedConversationPresets } =
    useSelector(getConversationState);
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [newPresetModal, setNewPresetModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState<
    ConversationPreset | undefined
  >();

  const styles = {
    container: {
      paddingBlock: 14,
      borderRadius: 14,
      backgroundColor: Colors.WHITE,
    },
    subtext: { fontSize: 12, color: Colors.GRAY1 },
    section: {
      paddingInline: 14,
    },
  };

  const contains = (preset: ConversationPreset, query: string) => {
    return preset.preset.toLowerCase().includes(query);
  };

  const conversationPresetsToShow = useMemo(() => {
    if (searchTerm === "") {
      return conversationPresets;
    }
    return filter(conversationPresets, (preset: ConversationPreset) => {
      return contains(preset, searchTerm);
    });
  }, [conversationPresets, searchTerm]);

  const handleOnDrag = useCallback(
    async (newItems: ConversationPreset[]) => {
      dispatch(conversationActions.setConversationPresets(newItems));
      await API.conversation.updateConversationPresetsOrder({
        userId: accountData.uid,
        conversationPresetIds: newItems.map((preset) => preset.id),
      });
    },
    [accountData.uid, dispatch]
  );

  return panelTabValue === 1 ? (
    <>
      <div
        className="RowNormal"
        style={{ padding: 30, gap: 20, alignItems: "flex-start" }}
      >
        <StandardBorderedContainer
          containerStyles={{ ...styles.container, width: 420 }}
        >
          <div className="ColumnNormal" style={{ ...styles.section, gap: 4 }}>
            <span className="sectionTitle">Conversation Presets</span>
            <span className="smallBodySubtext">
              Respond faster with conversation presets and AI suggestions
            </span>
          </div>
          <HorizontalDivider altMargin={14} />
          <div className="ColumnNormal" style={{ ...styles.section, gap: 4 }}>
            <span style={{ fontSize: 24, fontWeight: 500 }}>
              {conversationPresets.length}
            </span>
            <span className="smallBodySubtext">Presets</span>
          </div>
          {/* <HorizontalDivider altMargin={14} />
          <div style={styles.section}>
            <SwitchToggleItem
              title="Enable AI Suggestions"
              description="Enable and connect to Open AI for AI suggestions in conversations"
              disabled
              toggleValue={false}
              onChange={() => {}}
            />
          </div> */}
        </StandardBorderedContainer>
        <StandardBorderedContainer
          containerStyles={{
            ...styles.container,
            ...styles.section,
            flex: 1,
            height: 400,
          }}
        >
          <div
            className="ColumnNormal"
            style={{ gap: 20, height: "100%", minHeight: 0 }}
          >
            <div className="AlignedRowSpaced">
              <SearchBoxContainer
                placeholder={"Search"}
                onChange={(e) => setSearchTerm(e.target.value)}
                containerStyles={{ marginTop: 0, width: 300 }}
              />
              <div>
                <RectangleButton
                  buttonLabel="New Preset"
                  iconRight={
                    <Icon
                      icon="ion:add-circle"
                      height={18}
                      style={{ color: Colors.WHITE }}
                    />
                  }
                  altColor={Colors.BLACK}
                  altTextColor={Colors.WHITE}
                  containerStyles={{ padding: "12px 14px" }}
                  onPress={() => setNewPresetModal(true)}
                />
              </div>
            </div>
            <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
              {conversationPresetsToShow.length > 0 ? (
                <DragDropList
                  items={conversationPresetsToShow}
                  handleChange={handleOnDrag}
                >
                  {(
                    conversationPreset: ConversationPreset,
                    index: number,
                    dragHandleProps: any
                  ) => (
                    <div className="ColumnNormal" style={{ gap: 14 }}>
                      <div className="AlignedRow" style={{ gap: 14 }}>
                        <DragDropListIcon dragHandleProps={dragHandleProps} />
                        <ConversationPresetItem
                          key={conversationPreset.id}
                          item={conversationPreset}
                          editPreset={() => {
                            setEditingPreset(conversationPreset);
                            setNewPresetModal(true);
                          }}
                        />
                      </div>
                      {index !== conversationPresetsToShow.length - 1 ? (
                        <div style={{ paddingLeft: 41 }}>
                          <HorizontalDivider altMargin={0} />
                        </div>
                      ) : null}
                    </div>
                  )}
                </DragDropList>
              ) : !loadedConversationPresets ? (
                <div className="Centering" style={{ paddingTop: 100 }}>
                  <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
                </div>
              ) : (
                <EmptyStateFlatlist
                  searchTerm={searchTerm}
                  isLoading={!loadedConversationPresets}
                  nonSearchEmptyView={
                    <EmptyStateButton
                      title="No Presets"
                      description="Create a new conversation preset to select from when messaging your contacts"
                      icon={
                        <Icon
                          icon="ion:sparkles"
                          height={50}
                          style={{ color: Colors.GRAY1 }}
                        />
                      }
                      iconBox={70}
                      btnText="New Preset"
                      onPress={() => setNewPresetModal(true)}
                      containerStyles={{ paddingTop: 40 }}
                    />
                  }
                  containerStyles={{ paddingTop: 40 }}
                />
              )}
            </div>
          </div>
        </StandardBorderedContainer>
      </div>
      {newPresetModal ? (
        <ConversationPresetModal
          closeOnPress={() => {
            setNewPresetModal(false);
            setEditingPreset(undefined);
          }}
          existingPreset={editingPreset}
        />
      ) : null}
    </>
  ) : null;
};

export default ConversationPresetsPanel;
